import * as React from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';

import { IconLoadingButton, ClosableModal } from 'components';
import { PlusIcon } from 'assets/icons';

import { selectLoader, selectModalState } from 'base/common/add-content-modal/selector';
import { viewContentActions } from 'base/common/add-content-modal/slice';
import { ICreateContentModalForm, ModalStates } from 'base/common/add-content-modal/interface';
import { BlockType } from '@surge-global-engineering/form-builder-v2';
import contentModalFormValidation from '../validation/add-content-modal-form-validation';
import ContentModalForm from '../add-content-modal-form';

import './styles.scss';

interface Props { refreshContent: () => void, uploadBtnTxt?: string; blockType?: BlockType; }

const ContentModal = ({ refreshContent, uploadBtnTxt, blockType }: Props) => {
  const modalState = useSelector(selectModalState);
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const methods = useForm<ICreateContentModalForm>({
    defaultValues: {
      path: '/root',
      contentUrl: '',
      title: '',
      description: '',
      fileName: '',
      fileType: '',
      fileCategory: '',
      thumbnailGenerated: '',
    },
    resolver: yupResolver(contentModalFormValidation),
    shouldFocusError: true,
  });

  const handleOpen = () => dispatch(viewContentActions.openModal(ModalStates.addContent));
  const handleClose = () => {
    dispatch(viewContentActions.closeModal());
    methods.reset();
    if (refreshContent) {
      refreshContent();
    }
  };

  const onSubmit: SubmitHandler<ICreateContentModalForm> = React.useCallback((data) => {
    dispatch(viewContentActions.createContent(data));
    methods.reset();
  }, [dispatch]);

  return (
    <>
      <IconLoadingButton
        startIcon={<PlusIcon sx={{ color: 'primary.main' }} />}
        loading={loading}
        onClick={handleOpen}
        sx={{ p: 0 }}
      >
        {uploadBtnTxt}
      </IconLoadingButton>
      <ClosableModal
        open={modalState === ModalStates.addContent}
        onClose={handleClose}
        title="Upload media"
        subTitle=""
        modalClass="content-upload-modal"
      >
        <FormProvider {...methods}>
          <ContentModalForm
            onSubmit={onSubmit}
            loading={loading}
            closeModal={handleClose}
            blockType={blockType}
          />
        </FormProvider>
      </ClosableModal>
    </>
  );
};

ContentModal.defaultProps = {
  uploadBtnTxt: 'Upload media',
  blockType: undefined,
};

export default ContentModal;
